import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface NorthDocument {
  id: string;
  north_id: string;
  team_id: string;
  file_name: string;
  file_description: string | null;
  storage_path: string | null;
  public_url: string | null;
  link_url: string | null;
  file_size_bytes: number | null;
  created_at: string;
}

export function useNorthDocumentsQuery(northId: string | undefined) {
  return useQuery({
    queryKey: ['north-documents', northId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('north_documents')
        .select('*')
        .eq('north_id', northId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data || []) as NorthDocument[];
    },
    enabled: !!northId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
}

export function useAddNorthDocumentMutation(northId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (doc: Omit<NorthDocument, 'id' | 'north_id' | 'created_at'>) => {
      const { data, error } = await supabase
        .from('north_documents')
        .insert([{ ...doc, north_id: northId }])
        .select()
        .single();
      if (error) throw error;
      return data as NorthDocument;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['north-documents', northId] });
    },
  });
}

export function useDeleteNorthDocumentMutation(northId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (doc: NorthDocument) => {
      if (doc.storage_path) {
        await supabase.storage.from('logos').remove([doc.storage_path]);
      }
      const { error } = await supabase
        .from('north_documents')
        .delete()
        .eq('id', doc.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['north-documents', northId] });
    },
  });
}
